import * as THREE from 'three';
import {
  AnyPartData,
  HeadPartData,
  WeaponPartData,
  CorePartData,
  TransportPartData,
  Part,
  PartMeshFactory,
} from './Part.js';

/**
 * Central registry of all part definitions.
 *
 * This is the single source of truth for part stats and behaviors.
 * Parts are organized by slot type for easier navigation.
 */
export const PARTS: Record<string, AnyPartData> = {
  // ========== HEADS ==========
  // Heads affect perception and targeting.
  // MVP heads: Scanner, Tracker, Spotter

  scanner: {
    id: 'scanner',
    name: 'Scanner',
    slot: 'head',
    tags: ['precision', 'energy'],
    maxDurability: 50,
    tier: 0,
    visionRadius: 12,
    canSeeHP: true,
    canSeeTrails: false,
    canSeeDrops: true,
  } as HeadPartData,

  tracker: {
    id: 'tracker',
    name: 'Tracker',
    slot: 'head',
    tags: ['precision', 'sustained'],
    maxDurability: 50,
    tier: 0,
    visionRadius: 10,
    canSeeHP: false,
    canSeeTrails: true,
    canSeeDrops: false,
  } as HeadPartData,

  spotter: {
    id: 'spotter',
    name: 'Spotter',
    slot: 'head',
    tags: ['precision', 'light'],
    maxDurability: 40,
    tier: 0,
    visionRadius: 15, // Largest vision radius
    canSeeHP: false,
    canSeeTrails: false,
    canSeeDrops: false,
  } as HeadPartData,

  // ========== WEAPONS ==========
  // Weapons determine fire patterns and damage.
  // MVP weapons: Repeater, Lancer, Sprayer
  // Detailed stats will be filled in during weapon task

  repeater: {
    id: 'repeater',
    name: 'Repeater',
    slot: 'weapon',
    tags: ['sustained', 'precision'],
    maxDurability: 60,
    tier: 0,
    fireRate: 5, // 5 shots per second
    damage: 10,
    energyCost: 2,
    projectileSpeed: 15,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2,
    projectileColor: 0xffeb3b, // Yellow
  } as WeaponPartData,

  lancer: {
    id: 'lancer',
    name: 'Lancer',
    slot: 'weapon',
    tags: ['precision', 'heavy'],
    maxDurability: 70,
    tier: 0,
    fireRate: 1.5, // Slow shots
    damage: 30,
    energyCost: 10,
    projectileSpeed: 25,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2,
    projectileColor: 0x2196f3, // Blue
  } as WeaponPartData,

  sprayer: {
    id: 'sprayer',
    name: 'Sprayer',
    slot: 'weapon',
    tags: ['burst', 'thermal'],
    maxDurability: 50,
    tier: 0,
    fireRate: 8, // Very fast
    damage: 5,
    energyCost: 1,
    projectileSpeed: 10,
    spread: 30,
    projectilesPerShot: 3,
    projectileLifetime: 1.5,
    projectileColor: 0xff5722, // Orange
  } as WeaponPartData,

  seeker: {
    id: 'seeker',
    name: 'Seeker',
    slot: 'weapon',
    tags: ['precision', 'energy'],
    maxDurability: 55,
    tier: 0,
    fireRate: 2.5,
    damage: 12,
    energyCost: 6,
    projectileSpeed: 8, // Slower but homing
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 3,
    behavior: 'homing',
    homingStrength: 4,
    projectileColor: 0x00ffff, // Cyan
  } as WeaponPartData,

  ricochet: {
    id: 'ricochet',
    name: 'Ricochet',
    slot: 'weapon',
    tags: ['burst', 'light'],
    maxDurability: 55,
    tier: 0,
    fireRate: 3,
    damage: 8,
    energyCost: 4,
    projectileSpeed: 12,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 4, // Long lifetime for bouncing
    behavior: 'bouncing',
    maxBounces: 4,
    projectileColor: 0x00ff00, // Green
  } as WeaponPartData,

  splitter: {
    id: 'splitter',
    name: 'Splitter',
    slot: 'weapon',
    tags: ['burst', 'heavy'],
    maxDurability: 60,
    tier: 0,
    fireRate: 1.5,
    damage: 15,
    energyCost: 8,
    projectileSpeed: 10,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2,
    behavior: 'splitting',
    splitCount: 4,
    projectileColor: 0xff00ff, // Magenta
  } as WeaponPartData,

  mortar: {
    id: 'mortar',
    name: 'Mortar',
    slot: 'weapon',
    tags: ['heavy', 'burst', 'thermal'],
    maxDurability: 80,
    tier: 0,
    fireRate: 0.8, // Slow but devastating
    damage: 45,
    energyCost: 12,
    projectileSpeed: 8,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 1.5,
    behavior: 'arcing',
    arcHeight: 4,
    explosionRadius: 2.5,
    projectileColor: 0xff4400, // Orange-red
  } as WeaponPartData,

  needler: {
    id: 'needler',
    name: 'Needler',
    slot: 'weapon',
    tags: ['precision', 'sustained', 'light'],
    maxDurability: 60,
    tier: 0,
    fireRate: 10, // Very fast
    damage: 3,
    energyCost: 1,
    projectileSpeed: 20,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 0.8,
    behavior: 'piercing',
    pierceCount: 2,
    projectileColor: 0x00ff88, // Bright green
  } as WeaponPartData,

  shocker: {
    id: 'shocker',
    name: 'Shocker',
    slot: 'weapon',
    tags: ['energy', 'burst'],
    maxDurability: 70,
    tier: 0,
    fireRate: 2,
    damage: 8,
    energyCost: 8,
    projectileSpeed: 18,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 0.6,
    behavior: 'chain',
    chainTargets: 3,
    chainRange: 3,
    projectileColor: 0x00aaff, // Electric blue
  } as WeaponPartData,

  // ========== CORES ==========
  // Cores provide HP and energy.
  // MVP cores: Standard, Capacitor, Bulwark

  standard: {
    id: 'standard',
    name: 'Standard Core',
    slot: 'core',
    tags: ['sustained'],
    maxDurability: 80,
    tier: 0,
    maxHP: 100,
    energyRegen: 10, // 10 energy per second
  } as CorePartData,

  capacitor: {
    id: 'capacitor',
    name: 'Capacitor Core',
    slot: 'core',
    tags: ['energy', 'light'],
    maxDurability: 50,
    tier: 0,
    maxHP: 60,
    energyRegen: 25, // Fast regen
  } as CorePartData,

  bulwark: {
    id: 'bulwark',
    name: 'Bulwark Core',
    slot: 'core',
    tags: ['armor', 'heavy'],
    maxDurability: 120,
    tier: 0,
    maxHP: 150,
    energyRegen: 5, // Slow regen
  } as CorePartData,

  // ========== TRANSPORTS ==========
  // Transports affect movement and dash.
  // MVP transports: Treads, Hover, Legs

  treads: {
    id: 'treads',
    name: 'Treads',
    slot: 'transport',
    tags: ['heavy', 'armor'],
    maxDurability: 80,
    tier: 0,
    moveSpeed: 4, // Steady, knockback resistant
    dashDistance: 3, // Short dash
    dashEnergyCost: 15, // Low energy cost
  } as TransportPartData,

  hover: {
    id: 'hover',
    name: 'Hover',
    slot: 'transport',
    tags: ['light', 'energy'],
    maxDurability: 50,
    tier: 0,
    moveSpeed: 6, // Fast, frictionless
    dashDistance: 4, // Medium dash
    dashEnergyCost: 20, // Medium energy cost
  } as TransportPartData,

  legs: {
    id: 'legs',
    name: 'Legs',
    slot: 'transport',
    tags: ['sustained'],
    maxDurability: 70,
    tier: 0,
    moveSpeed: 5, // Medium speed
    dashDistance: 6, // Long distance dash
    dashEnergyCost: 25, // Higher cost for longer dash
  } as TransportPartData,

  // ========== TIER 1 FUSIONS ==========
  // Fused parts created by combining two base parts.
  // Generally more powerful than base parts with combined abilities.

  // === FUSED HEADS ===

  analyst: {
    id: 'analyst',
    name: 'Analyst',
    slot: 'head',
    tags: ['precision', 'precision', 'energy', 'sustained'], // Scanner + Tracker tags
    maxDurability: 60,
    tier: 1,
    visionRadius: 14, // Better than both parents
    canSeeHP: true, // From Scanner
    canSeeTrails: true, // From Tracker
    canSeeDrops: true, // From Scanner
  } as HeadPartData,

  oracle: {
    id: 'oracle',
    name: 'Oracle',
    slot: 'head',
    tags: ['precision', 'precision', 'energy', 'light'], // Scanner + Spotter tags
    maxDurability: 55,
    tier: 1,
    visionRadius: 16, // Largest vision - combines Scanner and Spotter
    canSeeHP: true, // From Scanner
    canSeeTrails: false,
    canSeeDrops: true, // From Scanner
  } as HeadPartData,

  predator: {
    id: 'predator',
    name: 'Predator',
    slot: 'head',
    tags: ['precision', 'precision', 'sustained', 'light'], // Tracker + Spotter tags
    maxDurability: 55,
    tier: 1,
    visionRadius: 15, // Good vision
    canSeeHP: false,
    canSeeTrails: true, // From Tracker
    canSeeDrops: true, // Enhanced awareness
  } as HeadPartData,

  // === FUSED WEAPONS ===

  railgun: {
    id: 'railgun',
    name: 'Railgun',
    slot: 'weapon',
    tags: ['sustained', 'precision', 'precision', 'heavy'], // Repeater + Lancer tags
    maxDurability: 75,
    tier: 1,
    fireRate: 2.5, // Between Repeater and Lancer
    damage: 25, // High damage
    energyCost: 8,
    projectileSpeed: 30, // Very fast
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2.5,
  } as WeaponPartData,

  storm: {
    id: 'storm',
    name: 'Storm',
    slot: 'weapon',
    tags: ['sustained', 'precision', 'burst', 'thermal'], // Repeater + Sprayer tags
    maxDurability: 65,
    tier: 1,
    fireRate: 7, // Very fast
    damage: 8, // Moderate per shot
    energyCost: 1.5,
    projectileSpeed: 12,
    spread: 20, // Some spread but less than Sprayer
    projectilesPerShot: 2, // Dual shots
    projectileLifetime: 1.8,
  } as WeaponPartData,

  nova: {
    id: 'nova',
    name: 'Nova',
    slot: 'weapon',
    tags: ['precision', 'heavy', 'burst', 'thermal'], // Lancer + Sprayer tags
    maxDurability: 70,
    tier: 1,
    fireRate: 1.0, // Slow but devastating
    damage: 15, // Moderate per projectile
    energyCost: 12,
    projectileSpeed: 15,
    spread: 45, // Wide spread
    projectilesPerShot: 5, // Shotgun blast
    projectileLifetime: 1.5,
  } as WeaponPartData,

  // Seeker + Repeater = Hunter (homing rapid fire)
  hunter: {
    id: 'hunter',
    name: 'Hunter',
    slot: 'weapon',
    tags: ['precision', 'energy', 'sustained'],
    maxDurability: 65,
    tier: 1,
    fireRate: 4,
    damage: 10,
    energyCost: 4,
    projectileSpeed: 10,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2.5,
    behavior: 'homing',
    homingStrength: 3,
    projectileColor: 0x00e5ff,
  } as WeaponPartData,

  // Ricochet + Sprayer = Chaos (bouncing spread)
  chaos: {
    id: 'chaos',
    name: 'Chaos',
    slot: 'weapon',
    tags: ['burst', 'light', 'thermal'],
    maxDurability: 60,
    tier: 1,
    fireRate: 5,
    damage: 6,
    energyCost: 3,
    projectileSpeed: 14,
    spread: 25,
    projectilesPerShot: 2,
    projectileLifetime: 3.5,
    behavior: 'bouncing',
    maxBounces: 3,
    projectileColor: 0x76ff03,
  } as WeaponPartData,

  // Splitter + Lancer = Hydra (piercing + splitting stacked!)
  // Pierces 2 enemies, then splits into 4 projectiles
  hydra: {
    id: 'hydra',
    name: 'Hydra',
    slot: 'weapon',
    tags: ['burst', 'heavy', 'precision'],
    maxDurability: 75,
    tier: 1,
    fireRate: 1.2,
    damage: 25, // Higher base for 2-modifier penalty
    energyCost: 12, // +25% energy
    projectileSpeed: 18,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2.5,
    modifiers: {
      piercing: { maxPierces: 2 },
      splitting: { count: 4, inheritModifiers: false }, // Children don't pierce
    },
    projectileColor: 0xaa00ff,
  } as WeaponPartData,

  // Seeker + Ricochet = Pinball (homing + bouncing stacked!)
  pinball: {
    id: 'pinball',
    name: 'Pinball',
    slot: 'weapon',
    tags: ['precision', 'energy', 'burst', 'light'],
    maxDurability: 60,
    tier: 1,
    fireRate: 2,
    damage: 12, // Higher base to compensate for 2-modifier penalty
    energyCost: 9, // +25% energy for stacking
    projectileSpeed: 10,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 4,
    modifiers: {
      homing: { strength: 2 },    // Curves toward enemies
      bouncing: { maxBounces: 5 }, // Bounces off walls
    },
    projectileColor: 0xffff00,
  } as WeaponPartData,

  // Repeater + Splitter = Cluster (rapid fire that splits)
  cluster: {
    id: 'cluster',
    name: 'Cluster',
    slot: 'weapon',
    tags: ['sustained', 'precision', 'burst', 'heavy'],
    maxDurability: 70,
    tier: 1,
    fireRate: 3,
    damage: 12,
    energyCost: 5,
    projectileSpeed: 12,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 1.8,
    behavior: 'splitting',
    splitCount: 3,
    projectileColor: 0xff6600,
  } as WeaponPartData,

  // Mortar + Repeater = Bombardier (rapid arcing shots)
  bombardier: {
    id: 'bombardier',
    name: 'Bombardier',
    slot: 'weapon',
    tags: ['heavy', 'burst', 'thermal', 'sustained'],
    maxDurability: 85,
    tier: 1,
    fireRate: 1.5,
    damage: 30,
    energyCost: 10,
    projectileSpeed: 10,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 1.3,
    behavior: 'arcing',
    arcHeight: 3,
    explosionRadius: 2,
    projectileColor: 0xff6600,
  } as WeaponPartData,

  // Mortar + Seeker = Smart Bomb (homing arcing explosives)
  smart_bomb: {
    id: 'smart_bomb',
    name: 'Smart Bomb',
    slot: 'weapon',
    tags: ['heavy', 'burst', 'thermal', 'precision', 'energy'],
    maxDurability: 75,
    tier: 1,
    fireRate: 0.6,
    damage: 50,
    energyCost: 15,
    projectileSpeed: 6,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2,
    behavior: 'arcing',
    arcHeight: 5,
    explosionRadius: 3,
    projectileColor: 0xff0066,
  } as WeaponPartData,

  // Mortar + Splitter = Cluster Bomb (arcing that splits into explosions)
  cluster_bomb: {
    id: 'cluster_bomb',
    name: 'Cluster Bomb',
    slot: 'weapon',
    tags: ['heavy', 'burst', 'thermal'],
    maxDurability: 80,
    tier: 1,
    fireRate: 0.5,
    damage: 35,
    energyCost: 18,
    projectileSpeed: 7,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 1.8,
    behavior: 'arcing',
    arcHeight: 4.5,
    explosionRadius: 3.5,
    projectileColor: 0xff2200,
  } as WeaponPartData,

  // Needler + Lancer = Railneedle (heavy piercing sniper)
  railneedle: {
    id: 'railneedle',
    name: 'Railneedle',
    slot: 'weapon',
    tags: ['precision', 'sustained', 'light', 'heavy'],
    maxDurability: 70,
    tier: 1,
    fireRate: 3,
    damage: 15,
    energyCost: 5,
    projectileSpeed: 28,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 1.2,
    behavior: 'piercing',
    pierceCount: 4,
    projectileColor: 0x00ffaa,
  } as WeaponPartData,

  // Needler + Sprayer = Shredder (piercing spread)
  shredder: {
    id: 'shredder',
    name: 'Shredder',
    slot: 'weapon',
    tags: ['precision', 'sustained', 'light', 'burst', 'thermal'],
    maxDurability: 65,
    tier: 1,
    fireRate: 8,
    damage: 4,
    energyCost: 1.5,
    projectileSpeed: 16,
    spread: 20,
    projectilesPerShot: 2,
    projectileLifetime: 0.9,
    behavior: 'piercing',
    pierceCount: 2,
    projectileColor: 0x88ff00,
  } as WeaponPartData,

  // Shocker + Seeker = Arc Hunter (homing + chain stacked!)
  // Projectile homes in, then chains on hit
  arc_hunter: {
    id: 'arc_hunter',
    name: 'Arc Hunter',
    slot: 'weapon',
    tags: ['energy', 'burst', 'precision'],
    maxDurability: 70,
    tier: 1,
    fireRate: 1.5,
    damage: 14, // Higher base for 2-modifier penalty
    energyCost: 12,
    projectileSpeed: 10,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 2,
    modifiers: {
      homing: { strength: 3 },
      chain: { targets: 3, range: 3.5, falloff: 0.75 },
    },
    projectileColor: 0x00ddff,
  } as WeaponPartData,

  // Shocker + Splitter = Storm Caller (splitting + chain stacked!)
  // Splits into multiple projectiles, each chains on hit
  storm_caller: {
    id: 'storm_caller',
    name: 'Storm Caller',
    slot: 'weapon',
    tags: ['energy', 'burst', 'heavy'],
    maxDurability: 75,
    tier: 1,
    fireRate: 0.8,
    damage: 12, // Higher base for penalty
    energyCost: 14,
    projectileSpeed: 12,
    spread: 0,
    projectilesPerShot: 1,
    projectileLifetime: 1.5,
    modifiers: {
      splitting: { count: 3, inheritModifiers: true }, // Children chain too!
      chain: { targets: 2, range: 3, falloff: 0.7 },
    },
    projectileColor: 0x0088ff,
  } as WeaponPartData,

  // === FUSED CORES ===

  dynamo: {
    id: 'dynamo',
    name: 'Dynamo Core',
    slot: 'core',
    tags: ['sustained', 'energy', 'light'], // Standard + Capacitor tags
    maxDurability: 70,
    tier: 1,
    maxHP: 85, // Balanced HP
    energyRegen: 18, // Very good energy regen
  } as CorePartData,

  fortress: {
    id: 'fortress',
    name: 'Fortress Core',
    slot: 'core',
    tags: ['sustained', 'armor', 'heavy'], // Standard + Bulwark tags
    maxDurability: 110,
    tier: 1,
    maxHP: 130, // Very high HP
    energyRegen: 8, // Decent energy
  } as CorePartData,

  reactor: {
    id: 'reactor',
    name: 'Reactor Core',
    slot: 'core',
    tags: ['energy', 'light', 'armor', 'heavy'], // Capacitor + Bulwark tags
    maxDurability: 90,
    tier: 1,
    maxHP: 110, // Good HP
    energyRegen: 15, // Good energy
  } as CorePartData,

  // === FUSED TRANSPORTS ===

  glider: {
    id: 'glider',
    name: 'Glider',
    slot: 'transport',
    tags: ['heavy', 'armor', 'light', 'energy'], // Treads + Hover tags
    maxDurability: 75,
    tier: 1,
    moveSpeed: 5.5, // Balanced speed
    dashDistance: 4.5, // Good dash
    dashEnergyCost: 15, // Efficient energy use
  } as TransportPartData,

  crawler: {
    id: 'crawler',
    name: 'Crawler',
    slot: 'transport',
    tags: ['heavy', 'armor', 'sustained'], // Treads + Legs tags
    maxDurability: 85,
    tier: 1,
    moveSpeed: 4.5, // Slower but steady
    dashDistance: 5, // Good dash distance
    dashEnergyCost: 20, // Moderate energy cost
  } as TransportPartData,

  strider: {
    id: 'strider',
    name: 'Strider',
    slot: 'transport',
    tags: ['light', 'energy', 'sustained'], // Hover + Legs tags
    maxDurability: 65,
    tier: 1,
    moveSpeed: 6.5, // Very fast
    dashDistance: 5.5, // Great dash distance
    dashEnergyCost: 22, // Slightly higher for better mobility
  } as TransportPartData,
};

/**
 * Create a new part instance from a part ID.
 *
 * The part starts at full durability and has no mesh (created on demand).
 *
 * @param partId - ID of the part to create (must exist in PARTS registry)
 * @returns A new Part instance
 * @throws Error if partId is not found in registry
 */
export function createPart(partId: string): Part {
  const data = PARTS[partId];
  if (!data) {
    throw new Error(`Part not found in registry: ${partId}`);
  }

  return {
    data,
    durability: data.maxDurability,
    mesh: null,
  };
}

/**
 * Get the part data for a given part ID.
 *
 * @param partId - ID of the part to look up
 * @returns The part data, or undefined if not found
 */
export function getPartData(partId: string): AnyPartData | undefined {
  return PARTS[partId];
}

// ========== MESH FACTORIES ==========
// These create the visual representation for each part type.
// For MVP, we use simple geometric shapes.

/**
 * Create a head mesh.
 *
 * Heads are cones that point in the direction the mech faces.
 * Different head types could have different cone sizes/colors.
 * Tier 1 heads are larger and have special colors.
 */
function createHeadMesh(part: Part): THREE.Object3D {
  const headData = part.data as HeadPartData;
  const group = new THREE.Group();

  // Size varies by tier and type
  let radius = 0.5;
  let height = 1;

  if (headData.tier === 1) {
    radius = 0.6; // Larger for fused parts
    height = 1.1;
  } else if (headData.id === 'spotter') {
    radius = 0.45; // Spotter is slightly smaller
  }

  const geometry = new THREE.ConeGeometry(radius, height, 16);

  // Color varies by type
  let color = 0x00bcd4; // Default teal

  // Base heads
  if (headData.id === 'scanner') {
    color = 0x00e5ff; // Brighter cyan
  } else if (headData.id === 'tracker') {
    color = 0x0097a7; // Darker teal
  } else if (headData.id === 'spotter') {
    color = 0x00acc1; // Medium teal
  }
  // Tier 1 fused heads - purple/pink tones for advanced tech
  else if (headData.id === 'analyst') {
    color = 0x9c27b0; // Purple - combines Scanner and Tracker
  } else if (headData.id === 'oracle') {
    color = 0xe91e63; // Pink - combines Scanner and Spotter
  } else if (headData.id === 'predator') {
    color = 0x673ab7; // Deep purple - combines Tracker and Spotter
  }

  const material = new THREE.MeshStandardMaterial({
    color,
    metalness: headData.tier === 1 ? 0.6 : 0.4, // More metallic for fused parts
    roughness: headData.tier === 1 ? 0.4 : 0.6,
  });

  const cone = new THREE.Mesh(geometry, material);

  // Rotate cone to point forward (+Z in local space)
  cone.rotation.x = Math.PI / 2;

  group.add(cone);
  return group;
}

/**
 * Create a weapon mesh.
 *
 * Each weapon has a distinct visual shape to be identifiable at a glance.
 */
function createWeaponMesh(part: Part): THREE.Object3D {
  const weaponData = part.data as WeaponPartData;
  const group = new THREE.Group();

  const isTier1 = weaponData.tier === 1;
  const scale = isTier1 ? 1.15 : 1.0;

  // Get weapon color from projectile color or use default
  const color = weaponData.projectileColor || 0x607d8b;

  const baseMat = new THREE.MeshStandardMaterial({
    color,
    metalness: isTier1 ? 0.7 : 0.5,
    roughness: isTier1 ? 0.3 : 0.5,
  });

  const accentMat = new THREE.MeshStandardMaterial({
    color: 0x333333,
    metalness: 0.8,
    roughness: 0.2,
  });

  switch (weaponData.id) {
    // === BASE WEAPONS ===
    case 'repeater': {
      // Long thin barrel - reliable workhorse
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08 * scale, 0.08 * scale, 1.0 * scale, 8),
        baseMat
      );
      barrel.rotation.z = Math.PI / 2;
      group.add(barrel);
      break;
    }

    case 'lancer': {
      // Thick tapered barrel with ring - heavy sniper
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06 * scale, 0.14 * scale, 1.2 * scale, 8),
        baseMat
      );
      barrel.rotation.z = Math.PI / 2;
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.16 * scale, 0.03 * scale, 8, 16),
        accentMat
      );
      ring.rotation.y = Math.PI / 2;
      ring.position.x = -0.3 * scale;
      group.add(barrel, ring);
      break;
    }

    case 'sprayer': {
      // Triple barrel - spread weapon
      for (let i = -1; i <= 1; i++) {
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.05 * scale, 0.05 * scale, 0.7 * scale, 6),
          baseMat
        );
        barrel.rotation.z = Math.PI / 2;
        barrel.position.y = i * 0.12 * scale;
        group.add(barrel);
      }
      break;
    }

    case 'seeker': {
      // Cone tip + cylinder body - homing missiles
      const body = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1 * scale, 0.1 * scale, 0.6 * scale, 8),
        baseMat
      );
      body.rotation.z = Math.PI / 2;
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.1 * scale, 0.3 * scale, 8),
        baseMat
      );
      cone.rotation.z = -Math.PI / 2;
      cone.position.x = 0.45 * scale;
      group.add(body, cone);
      break;
    }

    case 'ricochet': {
      // Angled barrel with deflector plate - bouncing
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08 * scale, 0.08 * scale, 0.8 * scale, 8),
        baseMat
      );
      barrel.rotation.z = Math.PI / 2;
      const plate = new THREE.Mesh(
        new THREE.BoxGeometry(0.02 * scale, 0.25 * scale, 0.2 * scale),
        accentMat
      );
      plate.position.set(0.3 * scale, 0.1 * scale, 0);
      plate.rotation.z = Math.PI / 4;
      group.add(barrel, plate);
      break;
    }

    case 'splitter': {
      // Wide muzzle that splits - splitting projectiles
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.15 * scale, 0.08 * scale, 0.8 * scale, 8),
        baseMat
      );
      barrel.rotation.z = Math.PI / 2;
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.12 * scale, 0.02 * scale, 8, 16),
        accentMat
      );
      ring.rotation.y = Math.PI / 2;
      ring.position.x = 0.35 * scale;
      group.add(barrel, ring);
      break;
    }

    case 'mortar': {
      // Short wide tube angled up - arcing AOE
      const tube = new THREE.Mesh(
        new THREE.CylinderGeometry(0.18 * scale, 0.12 * scale, 0.5 * scale, 8),
        baseMat
      );
      tube.rotation.z = Math.PI / 2 - 0.3; // Angled up
      const base = new THREE.Mesh(
        new THREE.BoxGeometry(0.3 * scale, 0.15 * scale, 0.25 * scale),
        accentMat
      );
      base.position.set(-0.2 * scale, -0.1 * scale, 0);
      group.add(tube, base);
      break;
    }

    case 'needler': {
      // Very long thin barrel - fast piercing
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04 * scale, 0.04 * scale, 1.3 * scale, 6),
        baseMat
      );
      barrel.rotation.z = Math.PI / 2;
      // Add small fins
      for (let i = 0; i < 3; i++) {
        const fin = new THREE.Mesh(
          new THREE.BoxGeometry(0.15 * scale, 0.02 * scale, 0.08 * scale),
          accentMat
        );
        fin.position.x = -0.3 * scale;
        fin.rotation.x = (i * Math.PI * 2) / 3;
        group.add(fin);
      }
      group.add(barrel);
      break;
    }

    case 'shocker': {
      // Spherical emitter with prongs - chain lightning
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.12 * scale, 8, 8),
        baseMat
      );
      sphere.position.x = 0.2 * scale;
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06 * scale, 0.08 * scale, 0.4 * scale, 6),
        accentMat
      );
      barrel.rotation.z = Math.PI / 2;
      barrel.position.x = -0.1 * scale;
      // Add prongs
      for (let i = 0; i < 3; i++) {
        const prong = new THREE.Mesh(
          new THREE.ConeGeometry(0.03 * scale, 0.15 * scale, 4),
          baseMat
        );
        prong.rotation.z = -Math.PI / 2;
        prong.position.x = 0.35 * scale;
        prong.position.y = Math.sin((i * Math.PI * 2) / 3) * 0.1 * scale;
        prong.position.z = Math.cos((i * Math.PI * 2) / 3) * 0.1 * scale;
        group.add(prong);
      }
      group.add(sphere, barrel);
      break;
    }

    // === TIER 1 FUSIONS - more elaborate versions ===
    default: {
      // For tier 1 or unknown weapons, create based on behavior
      const behavior = weaponData.behavior || 'normal';

      if (behavior === 'arcing') {
        // Mortar-style with extra detail
        const tube = new THREE.Mesh(
          new THREE.CylinderGeometry(0.2 * scale, 0.14 * scale, 0.6 * scale, 8),
          baseMat
        );
        tube.rotation.z = Math.PI / 2 - 0.25;
        const ring1 = new THREE.Mesh(
          new THREE.TorusGeometry(0.18 * scale, 0.025 * scale, 8, 16),
          accentMat
        );
        ring1.rotation.y = Math.PI / 2;
        ring1.position.x = 0.2 * scale;
        const base = new THREE.Mesh(
          new THREE.BoxGeometry(0.35 * scale, 0.18 * scale, 0.3 * scale),
          accentMat
        );
        base.position.set(-0.25 * scale, -0.12 * scale, 0);
        group.add(tube, ring1, base);
      } else if (behavior === 'chain') {
        // Shocker-style with more prongs
        const sphere = new THREE.Mesh(
          new THREE.SphereGeometry(0.15 * scale, 12, 12),
          baseMat
        );
        sphere.position.x = 0.25 * scale;
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08 * scale, 0.1 * scale, 0.5 * scale, 8),
          accentMat
        );
        barrel.rotation.z = Math.PI / 2;
        barrel.position.x = -0.1 * scale;
        for (let i = 0; i < 5; i++) {
          const prong = new THREE.Mesh(
            new THREE.ConeGeometry(0.035 * scale, 0.18 * scale, 4),
            baseMat
          );
          prong.rotation.z = -Math.PI / 2;
          prong.position.x = 0.42 * scale;
          prong.position.y = Math.sin((i * Math.PI * 2) / 5) * 0.12 * scale;
          prong.position.z = Math.cos((i * Math.PI * 2) / 5) * 0.12 * scale;
          group.add(prong);
        }
        group.add(sphere, barrel);
      } else if (behavior === 'piercing') {
        // Needler-style, longer
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.05 * scale, 0.05 * scale, 1.4 * scale, 8),
          baseMat
        );
        barrel.rotation.z = Math.PI / 2;
        const shroud = new THREE.Mesh(
          new THREE.CylinderGeometry(0.1 * scale, 0.1 * scale, 0.3 * scale, 8),
          accentMat
        );
        shroud.rotation.z = Math.PI / 2;
        shroud.position.x = -0.4 * scale;
        group.add(barrel, shroud);
      } else if (behavior === 'homing') {
        // Seeker-style with extra detail
        const body = new THREE.Mesh(
          new THREE.CylinderGeometry(0.12 * scale, 0.12 * scale, 0.7 * scale, 8),
          baseMat
        );
        body.rotation.z = Math.PI / 2;
        const cone = new THREE.Mesh(
          new THREE.ConeGeometry(0.12 * scale, 0.35 * scale, 8),
          baseMat
        );
        cone.rotation.z = -Math.PI / 2;
        cone.position.x = 0.5 * scale;
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(0.14 * scale, 0.02 * scale, 8, 16),
          accentMat
        );
        ring.rotation.y = Math.PI / 2;
        ring.position.x = 0.1 * scale;
        group.add(body, cone, ring);
      } else if (behavior === 'bouncing') {
        // Ricochet-style
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.1 * scale, 0.1 * scale, 0.9 * scale, 8),
          baseMat
        );
        barrel.rotation.z = Math.PI / 2;
        const plate = new THREE.Mesh(
          new THREE.BoxGeometry(0.03 * scale, 0.3 * scale, 0.25 * scale),
          accentMat
        );
        plate.position.set(0.35 * scale, 0.12 * scale, 0);
        plate.rotation.z = Math.PI / 4;
        group.add(barrel, plate);
      } else if (behavior === 'splitting') {
        // Splitter-style with bigger muzzle
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.18 * scale, 0.1 * scale, 0.9 * scale, 8),
          baseMat
        );
        barrel.rotation.z = Math.PI / 2;
        const ring1 = new THREE.Mesh(
          new THREE.TorusGeometry(0.15 * scale, 0.025 * scale, 8, 16),
          accentMat
        );
        ring1.rotation.y = Math.PI / 2;
        ring1.position.x = 0.4 * scale;
        const ring2 = new THREE.Mesh(
          new THREE.TorusGeometry(0.12 * scale, 0.02 * scale, 8, 16),
          accentMat
        );
        ring2.rotation.y = Math.PI / 2;
        ring2.position.x = 0.25 * scale;
        group.add(barrel, ring1, ring2);
      } else {
        // Default: dual barrel
        const barrel1 = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08 * scale, 0.08 * scale, 1.0 * scale, 8),
          baseMat
        );
        barrel1.rotation.z = Math.PI / 2;
        barrel1.position.y = 0.08 * scale;
        const barrel2 = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08 * scale, 0.08 * scale, 1.0 * scale, 8),
          baseMat
        );
        barrel2.rotation.z = Math.PI / 2;
        barrel2.position.y = -0.08 * scale;
        group.add(barrel1, barrel2);
      }
      break;
    }
  }

  return group;
}

/**
 * Create a core mesh.
 *
 * Cores are spheres at the center of the mech body.
 * Size varies by core type and tier.
 */
function createCoreMesh(part: Part): THREE.Object3D {
  const coreData = part.data as CorePartData;
  const group = new THREE.Group();

  // Size varies by core type and tier
  let radius = 0.4;

  // Base cores
  if (coreData.id === 'bulwark') {
    radius = 0.5; // Larger
  } else if (coreData.id === 'capacitor') {
    radius = 0.35; // Smaller
  }
  // Tier 1 cores are generally larger
  else if (coreData.id === 'fortress') {
    radius = 0.55; // Very large
  } else if (coreData.id === 'dynamo') {
    radius = 0.42; // Medium
  } else if (coreData.id === 'reactor') {
    radius = 0.48; // Large
  }

  const geometry = new THREE.SphereGeometry(radius, 16, 12);

  // Color varies by type
  let color = 0x4caf50; // Green for standard

  // Base cores
  if (coreData.id === 'capacitor') {
    color = 0x2196f3; // Blue for energy
  } else if (coreData.id === 'bulwark') {
    color = 0xff9800; // Orange for armor
  }
  // Tier 1 fused cores - vibrant colors
  else if (coreData.id === 'dynamo') {
    color = 0x00e5ff; // Bright cyan - energy focused
  } else if (coreData.id === 'fortress') {
    color = 0xffc107; // Amber - armor focused
  } else if (coreData.id === 'reactor') {
    color = 0x8bc34a; // Light green - balanced
  }

  const material = new THREE.MeshStandardMaterial({
    color,
    metalness: coreData.tier === 1 ? 0.5 : 0.3,
    roughness: coreData.tier === 1 ? 0.5 : 0.7,
  });

  const sphere = new THREE.Mesh(geometry, material);

  group.add(sphere);
  return group;
}

/**
 * Create a transport mesh.
 *
 * Transports are boxes at the base of the mech.
 * Shape varies by transport type and tier.
 */
function createTransportMesh(part: Part): THREE.Object3D {
  const transportData = part.data as TransportPartData;
  const group = new THREE.Group();

  let geometry: THREE.BufferGeometry | null = null;
  let color = 0x757575; // Gray default

  // Different shapes for different transports
  if (transportData.id === 'treads') {
    // Box for treads
    geometry = new THREE.BoxGeometry(1, 0.4, 1.2);
  } else if (transportData.id === 'hover') {
    // Flat cylinder for hover
    geometry = new THREE.CylinderGeometry(0.6, 0.6, 0.3, 16);
  } else if (transportData.id === 'legs') {
    // Legs - two boxes
    const leg1 = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.6, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x9e9e9e, metalness: 0.5, roughness: 0.5 })
    );
    leg1.position.set(-0.3, -0.3, 0);

    const leg2 = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.6, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x9e9e9e, metalness: 0.5, roughness: 0.5 })
    );
    leg2.position.set(0.3, -0.3, 0);

    group.add(leg1, leg2);
    return group;
  }
  // Tier 1 transports
  else if (transportData.id === 'glider') {
    // Hybrid hover/tread - wide flat box
    geometry = new THREE.BoxGeometry(1.2, 0.35, 1.4);
    color = 0x26c6da; // Light cyan
  } else if (transportData.id === 'crawler') {
    // Hybrid tread/legs - thick box with treads
    geometry = new THREE.BoxGeometry(1.1, 0.5, 1.3);
    color = 0x78909c; // Blue-gray
  } else if (transportData.id === 'strider') {
    // Hybrid hover/legs - elongated with slight taper
    const leg1 = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.7, 0.35),
      new THREE.MeshStandardMaterial({ color: 0xab47bc, metalness: 0.6, roughness: 0.4 })
    );
    leg1.position.set(-0.35, -0.35, 0);

    const leg2 = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.7, 0.35),
      new THREE.MeshStandardMaterial({ color: 0xab47bc, metalness: 0.6, roughness: 0.4 })
    );
    leg2.position.set(0.35, -0.35, 0);

    // Add hover pad between legs
    const hoverPad = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.5, 0.25, 16),
      new THREE.MeshStandardMaterial({ color: 0xce93d8, metalness: 0.6, roughness: 0.4 })
    );
    hoverPad.position.set(0, -0.5, 0);

    group.add(leg1, leg2, hoverPad);
    return group;
  }

  if (geometry) {
    const material = new THREE.MeshStandardMaterial({
      color,
      metalness: transportData.tier === 1 ? 0.6 : 0.5,
      roughness: transportData.tier === 1 ? 0.4 : 0.5,
    });

    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);
  }

  return group;
}

/**
 * Mesh factory registry.
 *
 * Maps slot types to their mesh creation functions.
 */
const MESH_FACTORIES: Record<string, PartMeshFactory> = {
  head: createHeadMesh,
  weapon: createWeaponMesh,
  core: createCoreMesh,
  transport: createTransportMesh,
};

/**
 * Create a mesh for the given part.
 *
 * Uses the appropriate factory based on the part's slot type.
 *
 * @param part - The part to create a mesh for
 * @returns A Three.js Object3D representing the part
 */
export function createPartMesh(part: Part): THREE.Object3D {
  const factory = MESH_FACTORIES[part.data.slot];
  if (!factory) {
    throw new Error(`No mesh factory for slot: ${part.data.slot}`);
  }
  return factory(part);
}
